/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ToolBarWidget", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * ToolBar widget.
   * @class ToolBarWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   * @publicdoc Widgets
   */
  cls.ToolBarWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.ToolBarWidget.prototype */ {
      __name: "ToolBarWidget",

      /**
       * Widget to use to scroll on toolbar
       * @type {classes.FlowDecoratorWidget}
       */
      _flowDecoratorWidget: null,

      _resizeHandler: null,

      /** @type {classes.FormWidget} **/
      _formWidget: null,

      /** @type {string} **/
      _position: null,

      toolbarAspectChange: "g_toolbarAspectChange",

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);
        this._flowDecoratorWidget = cls.WidgetFactory.createWidget("FlowDecorator", this.getBuildParameters());
        this._flowDecoratorWidget.setParentWidget(this);
        this._flowDecoratorWidget.setOrientation("vertical");
        this._flowDecoratorWidget.setRendering("list");
        this.getElement().querySelector(".mt-tab-flow").appendChild(this._flowDecoratorWidget.getElement());
        this._resizeHandler = context.HostService.onScreenResize(this._onResize.bind(this));
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        this._layoutInformation = new cls.LayoutInformation(this);
        this._layoutEngine = new cls.FlowLayoutEngine(this);
        this._layoutEngine.setFlowDecoratorWidget(this._flowDecoratorWidget);
      },

      /**
       * Handler called when screen is resized
       * @private
       */
      _onResize: function () {
        // A resize may follow a font/theme/density change that alters the 3-dots width.
        this._layoutEngine.invalidateDecoratorWidth();
        this._layoutEngine.forceMeasurement();
        this._layoutEngine.invalidateMeasure();
      },

      /**
       * Set Priority of this toolbar
       * @param {number} order the priority of this toolbar
       * @publicdoc
       */
      setOrder: function (order) {
        this.setStyle({
          order: order
        });
      },

      /**
       * Get Priority of this toolbar
       * @returns {number} priority of this toolbar
       * @publicdoc
       */
      getOrder: function () {
        return this.getStyle("order");
      },

      /**
       * Show/hide the text of the toolbar items
       * @param {boolean} state - true to display text under image, false to hide it
       * @publicdoc
       */
      setButtonTextHidden: function (state) {
        this.toggleClasses(state, "buttonTextHidden");
      },

      /**
       * Set toolbar button size
       * @param {String} buttonSize - size of the buttons
       */
      setButtonSize: function (buttonSize) {
        this.removeClass("button-small");
        this.removeClass("button-large");
        this.addClasses("button-" + buttonSize);

        // The button size drives the toolbar font-size, hence the 3-dots width.
        this._layoutEngine.invalidateDecoratorWidth();

        // re-measure children
        for (const item of this.getChildren()) {
          item?.getLayoutEngine()?.forceMeasurement();
          item?.getLayoutEngine()?.invalidateMeasure();
        }
        this._layoutEngine._refresh();
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._flowDecoratorWidget) {
          this._flowDecoratorWidget.destroy();
          this._flowDecoratorWidget = null;
        }
        if (this._resizeHandler) {
          this._resizeHandler();
          this._resizeHandler = null;
        }

        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target === null) {
          return true;
        } // just ignore qa click

        // walk up the DOM: the FlowDecorator root is a <button> wrapping an <i>, so a click
        // on the icon has the <i> as event target, not the widget root itself.
        if (!domEvent.target.closest(".gbc_FlowDecoratorWidget")) {
          this._flowDecoratorWidget.closeDropDown();
        }

        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;
        if (keyString === "esc") {
          //ESC key to close the dropdown of the continuous flow decorator
          if (this._flowDecoratorWidget && this._flowDecoratorWidget.isDropDownOpen()) {
            this._flowDecoratorWidget.closeDropDown();
            keyProcessed = true;
          }
        }
        return keyProcessed;
      },

      /**
       * Define the form linked to this toolbar
       * Mainly used for layout calculation in modal window
       * @param {classes.FormWidget} widget - the form widget to link
       */
      setFormWidget: function (widget) {
        this._formWidget = widget;
      },

      /**
       * Define the Aspect of the Toolbar
       * @param {String} mode - could be "icon", "text" or "both" (default)
       */
      setAspect: function (mode) {
        this._aspect = mode;
        this._element.setAttribute("gbc_toolbaraspect", mode);
        this.emit(this.toolbarAspectChange, mode);
      },

      /**
       *
       * @param {String} size - could be "small", ""
       */
      setItemSize: function (size) {},

      /**
       * @inheritDoc
       */
      setHidden: function (hidden) {
        hidden = this._position === "none" || hidden;
        $super.setHidden.call(this, hidden);
      },

      /**
       * Set toolbar items alignment
       * @param {string} alignment : 'left' (default), 'right', 'center', 'justify'
       */
      setToolBarItemsAlignment: function (alignment) {
        if (this._itemsAlignment !== alignment) {
          if (this._itemsAlignment) {
            this.getContainerElement().classList.remove(this._itemsAlignment);
          }
          this._itemsAlignment = alignment;
          if (this.getContainerElement()) {
            this.getContainerElement().classList.add(alignment);
          }
        }
      },

      /**
       * Store how the toolbar position should be handled
       * @returns {string} The position rule of the WindowWidget
       */
      getPosition: function () {
        return this._position;
      },

      setPosition: function (position) {
        this._position = position;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("ToolBar", cls.ToolBarWidget);
});
